import { promiseDelay } from '../../general/delay';
import { adSlotEvents } from './events';
import {
	moatIsOn,
	moatSafetyDataAvailable,
	moatSetTargetingForAllSlots,
	moatSetTargetingForSlot,
	moatSlotDataAvailable
} from '../moat'
import { adTechLog } from '../../general/log';
import { resolveAdSlots, getAdSlot, getAdSlots, updateAdSlotValue } from "../../general/ad-slot-storage";

const { googletagServices, moatDelayInSeconds } = window.adTechConfig;
const { enable_single_request, allow_section_ads_single_display } = googletagServices;

let moatDelayInSecondsFloat = parseFloat( moatDelayInSeconds );

// Set a default as no delay.
if ( isNaN( moatDelayInSecondsFloat ) ) {
	moatDelayInSecondsFloat = 0;
}

/**
 * Given a ad slot ID for multi request architecture, or nothing
 * for single request architecture, make sure that the
 * googletag.display() is sent with a ad slot.
 *
 * @param {Array|String} adSlots
 */
function requestAd( adSlots ) {

	// Add events at the point where we now that the ad element has been called.
	adSlotEvents();

	const lastSlotIndex = Object.keys( adSlots ).length - 1;

	// Get the last slot to be added to the queue.
	const adSlot = Object.values( adSlots ).reduce( ( obj, slot ) => {
		if ( slot.index === lastSlotIndex ) {
			return slot;
		}
	}, {} );

	// Reverse array to get the last slot for single display being turned off.
	// If its on, then only one ad will be in the array.
	adSlots = adSlots.reverse();

	for ( let i = 0; i < adSlots.length; i++ ) {

		let adSlot = adSlots[ i ];

		// Update the ad slot as requested.
		updateAdSlotValue( 'googletag', adSlot.id, 'status_step_2_request', true );

		// Display the ad slot, if.
		if ( allow_section_ads_single_display || ! allow_section_ads_single_display && 0 === i ) {
			window.googletag.display( adSlot.id );
		}
	}

	adTechLog( {
		type: 'pass',
		message: `GoogleTag - Display ad slots.`,
		details: {
			adSlots,
			enable_single_request,
			allow_section_ads_single_display,
		},
	} );

}

async function updateAdSlotStatus( adSlots ) {
	return Promise.all( adSlots.map( async( slot ) => {
		await updateAdSlotValue( 'googletag', slot.id, 'status_step_1_defined', true );
	} ) );
}

/**
 * Send all of the slots that have not rendered to Moat.
 *
 * @param {Array} adSlots The ad slots array or left empty to select all ad slots rendered.
 *
 * @returns {Promise<void[]>}
 */
async function sendAdSlotsToMoat( adSlots = [] ) {

	// If ad slots are not provided. Get the ad slots that have not rendered.
	if ( Array.isArray( adSlots ) && ! adSlots.length || ! adSlots ) {
		adSlots = getAdSlots( 'googletag', 'rendered', false );
	}

	// If this is a group of slots, log it.
	if ( adSlots.length > 1 ) {
		adTechLog( {
			type: 'info',
			message: `Moat - Ad slots being sent to Moat`,
			details: {
				adSlots
			}
		} );
	}

	return Promise.all( adSlots.map( async( slot ) => {
		await sendAdSlotToMoat( slot.id );
	} ) );

}

/**
 * Send a single ad slot to Moat.
 *
 * @param adSlotID
 */
function sendAdSlotToMoat( adSlotID ) {

	if ( ! adSlotID ) {
		return;
	}

	const adSlot = getAdSlot( 'googletag', adSlotID );

	if ( ! moatSlotDataAvailable( adSlot.slot ) ) {
		moatSetTargetingForSlot( adSlot.slot, adSlot.id );
	} else {
		adTechLog( {
			type: 'info',
			message: `Moat - Ad slot data is already available, for element id: "${ adSlotID }"`,
		} );
	}
}

/**
 * Ad slots must be an array of formatted ad slots. NOT GAM ad slots.
 * @param adSlots
 */
const googletagDisplayAdSlots = ( adSlots ) => {

	/**
	 * Steps.
	 * Update the ad slot status.
	 * Send the ad slots to moat.
	 * Delay for moat.
	 * Request the ads from GAM.
	 */
	Promise.all(
		[
			updateAdSlotStatus( adSlots ),
			sendAdSlotsToMoat( adSlots ),
			promiseDelay( moatDelayInSecondsFloat * 1000 ),
		]
	).then( () => {
		requestAd( adSlots );
	} );
}

const googletagDisplayAds = ( adSlots ) => {

	// If moat isn't on request the ad and bail early.
	if ( ! moatIsOn() ) {
		requestAd( adSlots );
		return;
	}

	// Run the targeting at a window level again if its
	// not set by checking "safetyDataAvailable".
	if ( ! moatSafetyDataAvailable() ) {
		adTechLog( {
			type: 'error',
			message: `GoogleTag - NOT safetyDataAvailable`,
		} );
		moatSetTargetingForAllSlots();
	}

	// Display all slots.
	googletagDisplayAdSlots( adSlots );
}

/**
 * Display a Google ad slot.
 *
 * @param {Array|String} adSlotIDs An array of ad slot IDs.
 */
export const googletagDisplay = ( adSlotIDs = [] ) => {

	// Ensure we are only handling arrays.
	if ( typeof adSlotIDs === 'string' || adSlotIDs instanceof String ) {
		adSlotIDs = [ adSlotIDs ];
	} else if ( ! Array.isArray( adSlotIDs ) ) {
		adSlotIDs = [];
	}


	if ( ! adSlotIDs.length ) {
		// No ad slot ids provided, call display on all slots.
		adTechLog( {
			type: 'error',
			message: `GoogleTag - googletagDisplay function used with no slots.`
		} );
		// TODO handle no ad slots passed in.
	} else {
		// Wait for the ad slots to be available, then send them.
		resolveAdSlots( adSlotIDs ).then( ( adSlots ) => {

			// Bail slots are not ready.
			if ( ! adSlots.length ) {
				return;
			}

			googletagDisplayAds( adSlots );
		} );
	}
}